import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AdminButton } from '@/components/admin/button';
import { AdminIcon } from '@/components/admin/icon';
import { Tag } from '@/components/admin/tag';
import { useConfirm, useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import {
  useCategoriesList,
  useEnhanceOneImage,
  useEnhanceProductImages,
  useProduct,
  useProductTransition,
  useProductsList,
  useUpdateProduct,
  useDeleteVariant,
} from '@/hooks/use-catalog';
import { usePageTitle } from '@/hooks/use-page-title';
import { usePermissions } from '@/hooks/use-permissions';
import { fmtFCFA } from '@/lib/format';
import { pluriel } from '@/lib/pluriel';
import { type CouleurImage, couleursParMedia } from '@/lib/catalog/apparence';
import { readProductOptions } from '@/lib/catalog/types';
import type { Product } from '@/lib/catalog/types';

/**
 * Revue du catalogue.
 *
 * Près de deux mille fiches attendent d'être relues puis publiées. Le détail
 * produit est fait pour travailler UNE fiche en profondeur : six cartes, du
 * défilement, un retour à la liste entre chaque. À ce rythme, le catalogue
 * n'ouvre jamais.
 *
 * Cet écran inverse la priorité. Tout ce qui sert à décider — visuels, titre,
 * description, catégorie, prix, déclinaisons — tient sur une hauteur d'écran,
 * sans défilement. Les corrections courantes se font sur place, et le geste
 * dominant (publier, passer au suivant) est au clavier. La fiche suivante est
 * préchargée pendant qu'on lit la courante : sur une connexion camerounaise,
 * c'est ce qui sépare un rythme de travail d'une attente permanente.
 */
export function AdminCatalogReviewPage() {
  usePageTitle('Revue du catalogue');
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const categorieId = params.get('categorie') ?? '';
  const [index, setIndex] = useState(0);
  const [sautes, setSautes] = useState<Set<string>>(new Set());

  // Toutes les catégories non archivées, actives ou non : une fiche rangée dans
  // une catégorie désactivée doit l'afficher, pas glisser sur la première option.
  const categories = useCategoriesList();
  const racines = useMemo(
    () => (categories.data ?? []).filter((c) => !c.parentId && c.isActive),
    [categories.data],
  );

  // Le périmètre se choisit par rayon mère, mais un produit vit le plus souvent
  // dans une sous-catégorie : n'offrir que les racines faisait afficher au
  // sélecteur de la fiche la PREMIÈRE option, donc une catégorie qui n'était
  // pas la sienne. L'arbre entier, indenté, dit la vérité.
  const arbre = useMemo(() => {
    const toutes = categories.data ?? [];
    const sortie: { id: string; name: string; profondeur: number }[] = [];
    const descendre = (parentId: string | null, profondeur: number) => {
      for (const c of toutes.filter((x) => (x.parentId ?? null) === parentId)) {
        sortie.push({ id: c.id, name: c.isActive ? c.name : `${c.name} (inactive)`, profondeur });
        descendre(c.id, profondeur + 1);
      }
    };
    descendre(null, 0);
    return sortie;
  }, [categories.data]);

  // La file se charge par tranches. Demander cent fiches d'un coup coûtait
  // sept secondes avant d'afficher quoi que ce soit, contre deux et demie pour
  // vingt-cinq : on ouvre vite, et la tranche suivante arrive pendant qu'on
  // travaille.
  const [page, setPage] = useState(1);
  // Les fiches déjà reçues ET le total annoncé voyagent ensemble : séparés, le
  // total se perdait pendant le chargement de la tranche suivante, qui porte
  // une autre clé de cache et vide donc `file.data` le temps de revenir.
  const [file_, setFile] = useState<{ items: Product[]; total: number }>({ items: [], total: 0 });

  const requete = useProductsList({
    status: 'draft',
    page,
    pageSize: 25,
    ...(categorieId ? { categoryId: categorieId } : {}),
  });

  // Le changement de rayon repart d'une file vide.
  useEffect(() => {
    setFile({ items: [], total: 0 });
    setPage(1);
  }, [categorieId]);

  useEffect(() => {
    const recu = requete.data;
    if (!recu) return;
    setFile((precedent) => {
      const connus = new Set(precedent.items.map((p) => p.id));
      const nouvelles = recu.items.filter((p) => !connus.has(p.id));
      return {
        items: nouvelles.length ? [...precedent.items, ...nouvelles] : precedent.items,
        total: recu.total,
      };
    });
  }, [requete.data]);

  const restants = useMemo(
    () => file_.items.filter((p) => !sautes.has(p.id)),
    [file_.items, sautes],
  );
  // Les fiches sautées sortent du dénominateur : on ne les traitera pas dans
  // cette passe, les annoncer gonflerait le reste à faire.
  const totalFile = Math.max(
    restants.length,
    (file_.total || restants.length) - sautes.size,
  );

  // On va chercher la tranche suivante avant d'arriver au bout, jamais au
  // moment où l'on en a besoin.
  useEffect(() => {
    const resteAVoir = restants.length - index;
    if (resteAVoir <= 6 && file_.items.length < totalFile && !requete.isFetching) {
      setPage((p) => p + 1);
    }
  }, [index, restants.length, file_.items.length, totalFile, requete.isFetching]);
  const courantId = restants[index]?.id;
  const suivantId = restants[index + 1]?.id;

  const produit = useProduct(courantId);
  // Précharge la fiche suivante : le clic sur « Publier » doit enchaîner, pas attendre.
  useProduct(suivantId);

  if (!courantId && !requete.isLoading) {
    return (
      <EcranTermine
        categorieId={categorieId}
        racines={racines}
        onChangerPerimetre={(v) => {
          setParams(v ? { categorie: v } : {});
          setIndex(0);
          setSautes(new Set());
        }}
        onQuitter={() => navigate('/catalogue')}
      />
    );
  }

  return (
    <FicheRevue
      key={courantId}
      produit={produit.data}
      arbre={arbre}
      chargement={produit.isLoading || requete.isLoading}
      position={index + 1}
      total={totalFile}
      categorieId={categorieId}
      racines={racines}
      onChangerPerimetre={(v) => {
        setParams(v ? { categorie: v } : {});
        setIndex(0);
        setSautes(new Set());
      }}
      onSauter={() => {
        if (courantId) setSautes((s) => new Set(s).add(courantId));
        toast.info('Fiche laissée de côté.');
      }}
      onPublie={(idPublie) => {
        // La fiche publiée sort de la file locale : l'index reste au même rang
        // et montre donc déjà la suivante, sans en sauter une.
        setFile((f) => ({ ...f, items: f.items.filter((p) => p.id !== idPublie), total: Math.max(0, f.total - 1) }));
      }}
      onQuitter={() => navigate('/catalogue')}
    />
  );
}

/* ── Fiche ──────────────────────────────────────────────────────────────── */

interface FicheProps {
  produit: Product | undefined;
  chargement: boolean;
  position: number;
  total: number;
  categorieId: string;
  racines: { id: string; name: string }[];
  arbre: { id: string; name: string; profondeur: number }[];
  onChangerPerimetre: (v: string) => void;
  onSauter: () => void;
  onPublie: (idPublie: string) => void;
  onQuitter: () => void;
}

function FicheRevue({
  produit,
  chargement,
  position,
  total,
  categorieId,
  racines,
  arbre,
  onChangerPerimetre,
  onSauter,
  onPublie,
  onQuitter,
}: FicheProps) {
  const toast = useToast();
  const [brouillon, setBrouillon] = useState<Partial<Product>>({});
  const majProduit = useUpdateProduct(produit?.id ?? '');
  const publier = useProductTransition('publish');
  const ameliorerTout = useEnhanceProductImages(produit?.id ?? '');
  const ameliorerUne = useEnhanceOneImage(produit?.id ?? '');
  const [imageEnCours, setImageEnCours] = useState<string | null>(null);
  const zoneRef = useRef<HTMLDivElement>(null);

  // Le brouillon repart de zéro à chaque fiche : une correction ne doit jamais
  // se reporter sur la suivante.
  useEffect(() => {
    setBrouillon({});
  }, [produit?.id]);

  const valeur = <K extends keyof Product>(cle: K): Product[K] | undefined =>
    (brouillon[cle] as Product[K] | undefined) ?? produit?.[cle];

  const modifie = Object.keys(brouillon).length > 0;

  const enregistrer = async (): Promise<boolean> => {
    if (!produit || !modifie) return true;
    try {
      await majProduit.mutateAsync({
        title: valeur('title') as string,
        shortDescription: (valeur('shortDescription') as string | null) ?? null,
        categoryId: valeur('categoryId') as string,
        basePrice: Number(valeur('basePrice') ?? 0),
        compareAtPrice: valeur('compareAtPrice') != null ? Number(valeur('compareAtPrice')) : null,
        metaTitle: (valeur('metaTitle') as string | null) ?? null,
        metaDescription: (valeur('metaDescription') as string | null) ?? null,
      });
      setBrouillon({});
      return true;
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Enregistrement impossible'));
      return false;
    }
  };

  const publierEtSuivant = async () => {
    if (!produit) return;
    if (!(await enregistrer())) return;
    try {
      await publier.mutateAsync(produit.id);
      toast.success(`${produit.title.slice(0, 40)}… est en ligne.`);
      onPublie(produit.id);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Publication impossible'));
    }
  };

  // Raccourcis : la main reste sur le clavier. Modificateur obligatoire, sinon
  // taper « p » dans une description publierait la fiche.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        void publierEtSuivant();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onSauter();
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        void enregistrer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const images = produit?.mediaLinks ?? [];
  // Quelle image montre quelle couleur : en revue on juge la photo avant de
  // lire le texte, et une déclinaison qui a hérité du visuel d'une autre ne se
  // voit autrement qu'en ouvrant la fiche complète.
  const titreSeo = ((valeur('metaTitle') as string | null) ?? produit?.title ?? '').trim();
  const descriptionSeo = (
    ((valeur('metaDescription') as string | null) ??
      (valeur('shortDescription') as string | null) ??
      '') as string
  ).trim();
  const nomRayon =
    arbre.find((c) => c.id === valeur('categoryId'))?.name ?? 'Catalogue';

  const couleurs = useMemo<Map<string, CouleurImage[]>>(
    () => (produit ? couleursParMedia(produit) : new Map()),
    [produit],
  );
  const occupe = majProduit.isPending || publier.isPending;

  return (
    <div className="revue" ref={zoneRef}>
      <header className="revue-barre">
        <div className="revue-titre">
          <AdminIcon n="eye" s={15} />
          <strong>Revue du catalogue</strong>
        </div>

        <label className="revue-perimetre">
          <span>Je relis</span>
          <select
            className="aform-i"
            value={categorieId}
            onChange={(e) => onChangerPerimetre(e.target.value)}
          >
            <option value="">Tous les rayons</option>
            {racines.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <div className="revue-compteur tn">
          {chargement ? '…' : `${position.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')}`}
          <small>{pluriel(total, 'fiche')} à relire</small>
        </div>

        <AdminButton variant="ghost" size="sm" onClick={onQuitter}>
          Quitter
        </AdminButton>
      </header>

      {chargement || !produit ? (
        <div className="revue-vide">Chargement de la fiche…</div>
      ) : (
        <div className="revue-corps">
          {/* Visuels */}
          <section className="revue-visuels">
            <div className="revue-galerie">
              {images.length === 0 ? (
                <div className="revue-sans-image">
                  <AdminIcon n="image" s={22} />
                  Aucune image — cette fiche ne devrait pas partir en ligne.
                </div>
              ) : (
                images.map((m) => (
                  <figure key={m.id} className="revue-vignette">
                    {m.url ? <img src={m.url} alt={m.altText ?? ''} loading="lazy" /> : null}
                    {(couleurs.get(m.mediaId) ?? []).length > 0 && (
                      <figcaption className="revue-vignette-couleur">
                        {(couleurs.get(m.mediaId) ?? []).map((c) => (
                          <span key={c.label} className="revue-vignette-couleur-item">
                            <i style={c.hex ? { background: c.hex } : undefined} />
                            {c.label}
                          </span>
                        ))}
                      </figcaption>
                    )}
                    <button
                      type="button"
                      className="revue-ia-image"
                      title="Reprendre cette image par l'IA"
                      disabled={ameliorerUne.isPending}
                      onClick={async () => {
                        setImageEnCours(m.mediaId);
                        try {
                          await ameliorerUne.mutateAsync(m.mediaId);
                          toast.success('Image reprise.');
                        } catch (e) {
                          toast.error(extractErrorMessage(e, 'Reprise impossible'));
                        } finally {
                          setImageEnCours(null);
                        }
                      }}
                    >
                      {imageEnCours === m.mediaId ? '…' : <AdminIcon n="star" s={12} />}
                    </button>
                  </figure>
                ))
              )}
            </div>

            <AdminButton
              variant="ghost"
              size="sm"
              disabled={!!produit.aiEnhancedAt || ameliorerTout.isPending || images.length === 0}
              title={
                produit.aiEnhancedAt
                  ? 'Déjà repris une fois en entier — reprenez image par image.'
                  : undefined
              }
              onClick={async () => {
                try {
                  await ameliorerTout.mutateAsync();
                  toast.success('Toutes les images ont été reprises.');
                } catch (e) {
                  toast.error(extractErrorMessage(e, 'Reprise impossible'));
                }
              }}
            >
              <AdminIcon n="star" s={13} />
              {ameliorerTout.isPending
                ? 'Reprise en cours…'
                : produit.aiEnhancedAt
                  ? 'Images déjà reprises'
                  : `Reprendre les ${images.length} images`}
            </AdminButton>
          </section>

          {/* Contenu éditable */}
          <section className="revue-contenu">
            <input
              className="revue-champ-titre"
              value={(valeur('title') as string) ?? ''}
              onChange={(e) => setBrouillon((b) => ({ ...b, title: e.target.value }))}
              placeholder="Titre du produit"
              aria-label="Titre"
            />

            <div className="revue-ligne">
              <label className="revue-bloc">
                <span>Rayon</span>
                <select
                  className="aform-i"
                  value={(valeur('categoryId') as string) ?? ''}
                  onChange={(e) => setBrouillon((b) => ({ ...b, categoryId: e.target.value }))}
                >
                  {/* Une valeur absente des options fait afficher la première par le
                      navigateur : la fiche paraissait rangée dans un rayon qui n'était
                      pas le sien. Une catégorie archivée reste donc visible, à reclasser. */}
                  {valeur('categoryId') &&
                    !arbre.some((c) => c.id === valeur('categoryId')) && (
                      <option value={valeur('categoryId') as string}>
                        Catégorie archivée — à reclasser
                      </option>
                    )}
                  {arbre.map((c) => (
                    <option key={c.id} value={c.id}>
                      {`${'\u00a0\u00a0'.repeat(c.profondeur)}${c.profondeur ? '└ ' : ''}${c.name}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="revue-bloc revue-bloc-prix">
                <span>Prix de vente (FCFA)</span>
                <input
                  className="aform-i tn"
                  type="number"
                  min={0}
                  value={Number(valeur('basePrice') ?? 0)}
                  onChange={(e) =>
                    setBrouillon((b) => ({ ...b, basePrice: Number(e.target.value) }))
                  }
                />
              </label>

              <label className="revue-bloc revue-bloc-prix">
                <span>Prix barré (FCFA)</span>
                <input
                  className="aform-i tn"
                  type="number"
                  min={0}
                  placeholder="—"
                  value={valeur('compareAtPrice') != null ? Number(valeur('compareAtPrice')) : ''}
                  onChange={(e) =>
                    setBrouillon((b) => ({
                      ...b,
                      compareAtPrice: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
              </label>
            </div>

            <Declinaisons produit={produit} />

            <label className="revue-bloc revue-bloc-plein">
              <span>Description courte — c'est elle qui s'affiche sous le titre en boutique</span>
              <textarea
                className="aform-i"
                rows={3}
                value={(valeur('shortDescription') as string) ?? ''}
                onChange={(e) => setBrouillon((b) => ({ ...b, shortDescription: e.target.value }))}
                placeholder="Deux phrases qui disent ce que le produit fait et pour qui."
              />
            </label>

            {/* Le référencement se décidait jusqu'ici dans la fiche complète,
                deux écrans plus loin : relire mille produits sans jamais voir
                ce que Google affichera revenait à publier à l'aveugle. */}
            <div className="revue-seo">
              <div className="revue-seo-tete">
                <AdminIcon n="search" s={13} />
                <span>Ce que verra Google</span>
                <small>
                  <b className={titreSeo.length > 60 ? 'revue-seo-trop' : undefined}>
                    {titreSeo.length} / 60
                  </b>
                  {' · '}
                  <b className={descriptionSeo.length > 160 ? 'revue-seo-trop' : undefined}>
                    {descriptionSeo.length} / 160
                  </b>
                </small>
              </div>

              <label className="revue-bloc">
                <span>Titre de recherche</span>
                <input
                  className="aform-i"
                  value={(valeur('metaTitle') as string | null) ?? ''}
                  maxLength={70}
                  onChange={(e) =>
                    setBrouillon((b) => ({ ...b, metaTitle: e.target.value || null }))
                  }
                  placeholder={produit.title}
                />
              </label>

              <label className="revue-bloc">
                <span>Description de recherche</span>
                <textarea
                  className="aform-i revue-seo-desc"
                  rows={2}
                  maxLength={180}
                  value={(valeur('metaDescription') as string | null) ?? ''}
                  onChange={(e) =>
                    setBrouillon((b) => ({ ...b, metaDescription: e.target.value || null }))
                  }
                  placeholder={(valeur('shortDescription') as string) ?? ''}
                />
              </label>

              <p className="revue-seo-apercu">
                <strong>{titreSeo || 'Sans titre'}</strong>
                <span>akwaba.cm › {nomRayon}</span>
                <em>{descriptionSeo || 'Aucune description — Google en inventera une.'}</em>
              </p>
            </div>
          </section>
        </div>
      )}

      <footer className="revue-pied">
        <AdminButton variant="ghost" onClick={onSauter} disabled={occupe}>
          Laisser de côté
          <kbd className="revue-kbd">⌘→</kbd>
        </AdminButton>

        <div className="revue-pied-droite">
          {modifie && <span className="revue-modifie">Modifications non enregistrées</span>}
          <AdminButton variant="secondary" onClick={() => void enregistrer()} disabled={!modifie || occupe}>
            Enregistrer
            <kbd className="revue-kbd">⌘S</kbd>
          </AdminButton>
          <AdminButton variant="primary" onClick={() => void publierEtSuivant()} disabled={occupe || !produit}>
            {publier.isPending ? 'Publication…' : 'Publier et continuer'}
            <kbd className="revue-kbd">⌘⏎</kbd>
          </AdminButton>
        </div>
      </footer>
    </div>
  );
}

/* ── Déclinaisons, en lecture ───────────────────────────────────────────── */

function Declinaisons({ produit }: { produit: Product }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { has, isSuperAdmin } = usePermissions();
  const supprimer = useDeleteVariant(produit.id);
  const [enCours, setEnCours] = useState<string | null>(null);

  const axe = readProductOptions(produit.attributes).find((o) => o.type === 'color');
  if (!produit.variants.length) return null;

  const peutSupprimer = isSuperAdmin || has('CATALOG.DELETE');
  // Le produit a besoin d'au moins une déclinaison pour rester vendable : la
  // dernière ne se retire pas d'ici.
  const retirable = peutSupprimer && produit.variants.length > 1;

  const retirer = async (v: Product['variants'][number]) => {
    const ok = await confirm({
      title: `Retirer « ${v.name} »`,
      message: (
        <>
          Cette déclinaison part avec son stock, ramené à zéro. L'import en
          invente parfois qui n'existent pas chez le fabricant.
        </>
      ),
      confirmLabel: 'Retirer',
      tone: 'danger',
    });
    if (!ok) return;
    setEnCours(v.id);
    try {
      await supprimer.mutateAsync({ id: v.id, force: true });
      toast.success(`« ${v.name} » retirée.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="revue-variantes">
      <span className="revue-variantes-l">
        {pluriel(produit.variants.length, 'déclinaison')}
      </span>
      <div className="revue-variantes-liste">
        {produit.variants.map((v) => {
          const label = axe ? v.optionValues?.[axe.name] : undefined;
          const hex = axe?.values.find((x) => x.label === label)?.color;
          return (
            <span
              key={v.id}
              className="revue-variante"
              title={`${v.sku} · ${fmtFCFA(produit.basePrice)}`}
            >
              {hex && <i className="variant-pastille" style={{ background: hex }} aria-hidden="true" />}
              {v.name}
              {!v.isActive && <Tag variant="neutral">inactive</Tag>}
              {retirable && (
                <button
                  type="button"
                  className="revue-variante-x"
                  aria-label={`Retirer ${v.name}`}
                  title="Retirer cette déclinaison"
                  disabled={enCours === v.id}
                  onClick={() => void retirer(v)}
                >
                  {enCours === v.id ? '…' : '×'}
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ── Fin de file ────────────────────────────────────────────────────────── */

function EcranTermine({
  categorieId,
  racines,
  onChangerPerimetre,
  onQuitter,
}: {
  categorieId: string;
  racines: { id: string; name: string }[];
  onChangerPerimetre: (v: string) => void;
  onQuitter: () => void;
}) {
  const rayon = racines.find((c) => c.id === categorieId)?.name;
  return (
    <div className="revue">
      <div className="revue-fini">
        <AdminIcon n="check" s={26} />
        <h2>{rayon ? `Le rayon ${rayon} est à jour` : 'Plus aucun brouillon à relire'}</h2>
        <p>
          {rayon
            ? 'Choisissez un autre rayon pour continuer, ou passez au catalogue entier.'
            : 'Toutes les fiches en brouillon sont passées. Les nouvelles arriveront ici.'}
        </p>
        <div className="revue-fini-actions">
          <select
            className="aform-i"
            value={categorieId}
            onChange={(e) => onChangerPerimetre(e.target.value)}
          >
            <option value="">Tous les rayons</option>
            {racines.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <AdminButton variant="primary" onClick={onQuitter}>
            Retour au catalogue
          </AdminButton>
        </div>
      </div>
    </div>
  );
}
